// Code generated by balancegen from data/presentation/light.toml. DO NOT EDIT.
// Regenerate with scripts/gen-data.sh.
//
// The light identity of the map canvas (ui-map-interface.md §2.5,
// R3293): the rule, every map ink in both identities, and the four
// depth band composites. balancegen computes every light ink from its
// dark ink with LIGHT_RULE; ui/lightrule.ts is the twin of the rule.

/** The rule of the light identity and its parameters. */
export const LIGHT_RULE = {
  rule: 'ladder_own_chroma',
  ladder: {
    paper: 0.96,
    top: 0.95,
    foot: 0.975,
    shelf: 0.865,
    slope: 0.905,
    deepb: 0.93,
    abyss: 0.955,
    rock: 0.8,
    rim: 0.45,
    hole: 0.98,
    trim: 0.5,
    text: 0.3,
    dim: 0.46,
    faint: 0.56,
    neutral: 0.6,
    mark: 0.55,
    core: 0.3,
    core2: 0.36,
    hot: 0.52,
    halo: 0.74,
    resource: 0.55,
    verdict: 0.52,
  } as Readonly<Record<string, number>>,
  paperH: 88,
  paperC: 0.03,
  paperRoles: ['paper', 'top', 'foot', 'abyss', 'hole', 'rock'] as readonly string[],
  hueMid: [1] as readonly number[],
  factionL: 0.62,
  chromaK: 1.1,
  glowAlphaK: 0.5,
  ramp: { lo: 0.45, hi: 0.85 },
} as const;

/** One map ink in both identities. A step of an ink list has the path
 *  `<list>.<i>`. */
export interface InkPair {
  readonly path: string;
  readonly role: string;
  readonly dark: number;
  readonly light: number;
}

/** Every map ink the generator emits, in the order of the registry
 *  of balancegen (light_sources.go). */
export const INK_PAIRS: readonly InkPair[] = [
  { path: 'water.deep_top', role: 'top', dark: 0x07223a, light: 0xf7eed8 },
  { path: 'water.deep_mid', role: 'paper', dark: 0x051829, light: 0xfaf1dc },
  { path: 'water.deep_low', role: 'foot', dark: 0x020b12, light: 0xfff6e1 },
  { path: 'water.vignette_color', role: 'foot', dark: 0x01060c, light: 0xfff6e1 },
  { path: 'depth.band.shelf', role: 'shelf', dark: 0x183349, light: 0xb7d7f3 },
  { path: 'depth.band.slope', role: 'slope', dark: 0x0d2437, light: 0xc7e4fd },
  { path: 'depth.band.deepb', role: 'deepb', dark: 0x06182a, light: 0xd7eaff },
  { path: 'depth.band.abyss', role: 'abyss', dark: 0x051220, light: 0xf9f0da },
  { path: 'wake.color', role: 'dim', dark: 0x86f7d0, light: 0x01684f },
  { path: 'haze.color', role: 'halo', dark: 0x2e5670, light: 0x86b1ce },
  { path: 'quake.silt_color', role: 'halo', dark: 0xc9b79a, light: 0xbaa88c },
  { path: 'map.neutral', role: 'neutral', dark: 0x88a0b0, light: 0x6d8493 },
  { path: 'map.marker_neutral', role: 'core', dark: 0xffffff, light: 0x2e2e2e },
  { path: 'accent.color', role: 'faction', dark: 0x35e0d2, light: 0x1395b5 },
  { path: 'accent.bright', role: 'core2', dark: 0xdffcf8, light: 0x2a4240 },
  { path: 'palette.text', role: 'text', dark: 0xa3cfe6, light: 0x053245 },
  { path: 'palette.dim', role: 'dim', dark: 0x5e89a0, light: 0x345e73 },
  { path: 'palette.faint', role: 'faint', dark: 0x4c7b93, light: 0x4c7b93 },
  { path: 'palette.panel', role: 'top', dark: 0x061623, light: 0xf7eed8 },
  { path: 'palette.deep', role: 'foot', dark: 0x020b12, light: 0xfff6e1 },
  { path: 'palette.bad', role: 'verdict', dark: 0xff5c8a, light: 0xbe1356 },
  { path: 'palette.good', role: 'verdict', dark: 0x7ee787, light: 0x007f25 },
  { path: 'palette.gold', role: 'verdict', dark: 0xd8c66a, light: 0x796904 },
  { path: 'palette.npc', role: 'core2', dark: 0xe8f3ff, light: 0x353e47 },
  { path: 'water.snow_color', role: 'core', dark: 0xbedceb, light: 0x18313d },
  { path: 'heat.modes.off.0', role: 'ramp', dark: 0x8fa6b4, light: 0x7c93a1 },
  { path: 'heat.modes.sov.0', role: 'ramp', dark: 0x1fe3c0, light: 0x076453 },
  { path: 'heat.modes.sov.1', role: 'ramp', dark: 0x12816d, light: 0x84e3cc },
  { path: 'heat.modes.activity.0', role: 'ramp', dark: 0xffc74d, light: 0xb58710 },
  { path: 'heat.modes.activity_season.0', role: 'ramp', dark: 0xffc74d, light: 0x6d5008 },
  { path: 'heat.modes.activity_season.1', role: 'ramp', dark: 0x6e4f14, light: 0xebc88f },
  { path: 'heat.modes.kills.0', role: 'ramp', dark: 0xff5a4d, light: 0xf24d42 },
  { path: 'heat.modes.currents.0', role: 'ramp', dark: 0x38c8f0, light: 0x139ec1 },
  { path: 'heat.modes.beacon.0', role: 'ramp', dark: 0xa97dff, light: 0x9d70f1 },
  { path: 'heat.modes.depth.0', role: 'ramp', dark: 0x7090ff, light: 0x6786f4 },
  { path: 'heat.modes.stability.0', role: 'ramp', dark: 0x5aa8ff, light: 0x4492e7 },
  { path: 'sonar.ping_color', role: 'mark', dark: 0x6ef7ea, light: 0x0c837a },
  { path: 'sonar.hostile', role: 'hot', dark: 0xff8080, light: 0xb1393f },
  { path: 'fleets.cargo_color', role: 'resource', dark: 0xc57a52, light: 0xa45c34 },
  { path: 'fleets.anchor_gold', role: 'mark', dark: 0xffd166, light: 0x8e6c05 },
  { path: 'fleets.shield_color', role: 'core2', dark: 0xe8f3ff, light: 0x353e47 },
  { path: 'fleets.stack.color', role: 'core2', dark: 0xe8f3ff, light: 0x353e47 },
  { path: 'fleets.bars.ground', role: 'paper', dark: 0x0a1620, light: 0xfaf1dc },
  { path: 'fleets.orders.move', role: 'mark', dark: 0xd8f4f0, light: 0x5e7774 },
  { path: 'fleets.orders.attack', role: 'mark', dark: 0xc0392b, light: 0xc23b2d },
  { path: 'fleets.orders.home', role: 'mark', dark: 0xa3cfe6, light: 0x4e788d },
  { path: 'fleets.orders.escort', role: 'mark', dark: 0xb18cff, light: 0x7d57c4 },
  { path: 'fleets.orders.anchor', role: 'mark', dark: 0xff5c8a, light: 0xc9235e },
  { path: 'fleets.orders.search', role: 'mark', dark: 0xd4f55a, light: 0x687c00 },
  { path: 'fleets.orders.commission', role: 'mark', dark: 0xff7ad9, light: 0xb53495 },
  { path: 'palette.amber', role: 'verdict', dark: 0xffb454, light: 0x915b00 },
  { path: 'deposit.ghost_ink', role: 'dim', dark: 0x56646e, light: 0x4c5a64 },
  { path: 'deposit.strike_ink', role: 'neutral', dark: 0x8fa3b0, light: 0x708390 },
  { path: 'deposit.tag.ink', role: 'core', dark: 0x04121d, light: 0x20303c },
  { path: 'damage.taken_color', role: 'hot', dark: 0xff4a24, light: 0xbd2802 },
  { path: 'damage.dealt_color', role: 'core2', dark: 0xe8f3ff, light: 0x353e47 },
  { path: 'seaobjects.target_bracket_color', role: 'hot', dark: 0xff4a3d, light: 0xc50c0d },
  { path: 'spawn.deep_top', role: 'top', dark: 0x020c14, light: 0xf7eed8 },
  { path: 'spawn.deep_mid', role: 'paper', dark: 0x010810, light: 0xfaf1dc },
  { path: 'spawn.deep_low', role: 'foot', dark: 0x000407, light: 0xfff6e1 },
  { path: 'spawn.cell_tint', role: 'top', dark: 0x0a2033, light: 0xf7eed8 },
  { path: 'spawn.haze_top', role: 'top', dark: 0x09283e, light: 0xf7eed8 },
  { path: 'spawn.haze_mid', role: 'paper', dark: 0x051a2c, light: 0xfaf1dc },
  { path: 'spawn.haze_low', role: 'foot', dark: 0x020c16, light: 0xfff6e1 },
  { path: 'spawn.quake_ring_bright', role: 'core', dark: 0xeafffb, light: 0x21322f },
  { path: 'spawn.quake_ring_dim', role: 'mark', dark: 0x9fe8f5, light: 0x347d89 },
  { path: 'creatures.whale_tint', role: 'shelf', dark: 0x0d2233, light: 0xbcd6ed },
  { path: 'creatures.kraken_ink', role: 'core', dark: 0x000407, light: 0x243035 },
  { path: 'creatures.kraken_mark', role: 'text', dark: 0xa3cfe6, light: 0x053245 },
  { path: 'mark_inks.white', role: 'core', dark: 0xffffff, light: 0x2e2e2e },
  { path: 'mark_inks.halo', role: 'paper', dark: 0x01060b, light: 0xfaf1dc },
  { path: 'mark_inks.hold_body', role: 'top', dark: 0x223a4c, light: 0xf7eed8 },
  { path: 'mark_inks.lantern', role: 'top', dark: 0x0b1b28, light: 0xf7eed8 },
  { path: 'mark_inks.ruin_ink', role: 'faint', dark: 0x3a4854, light: 0x677784 },
  { path: 'mark_inks.ruin_fill', role: 'rock', dark: 0x16222c, light: 0xc6bda8 },
  { path: 'mark_inks.caustic', role: 'core', dark: 0xe6fffb, light: 0x1e3230 },
  { path: 'torpedo.white', role: 'core', dark: 0xffffff, light: 0x2e2e2e },
  { path: 'torpedo.amber', role: 'hot', dark: 0xffb454, light: 0x915b00 },
  { path: 'torpedo.hot', role: 'hot', dark: 0xff4030, light: 0xc60501 },
  { path: 'torpedo.plume', role: 'halo', dark: 0xffd9a0, light: 0xc9a46d },
  { path: 'torpedo.foam', role: 'core2', dark: 0xcfe8f2, light: 0x2c4149 },
  { path: 'torpedo.steel', role: 'core2', dark: 0xdff6ff, light: 0x2e4048 },
  { path: 'detonation.limits.white', role: 'core', dark: 0xffffff, light: 0x2e2e2e },
  { path: 'detonation.limits.fire', role: 'hot', dark: 0xffa864, light: 0x9c5306 },
  { path: 'detonation.limits.amber', role: 'hot', dark: 0xffb454, light: 0x915b00 },
  { path: 'detonation.limits.hot', role: 'hot', dark: 0xff4030, light: 0xc60501 },
  { path: 'detonation.limits.plume', role: 'halo', dark: 0xffd9a0, light: 0xc9a46d },
  { path: 'detonation.limits.foam', role: 'core2', dark: 0xcfe8f2, light: 0x2c4149 },
  { path: 'detonation.limits.steel', role: 'core2', dark: 0xdff6ff, light: 0x2e4048 },
  { path: 'detonation.limits.water', role: 'top', dark: 0x0a2739, light: 0xf7eed8 },
  { path: 'holdfire.limits.ink', role: 'text', dark: 0xa3cfe6, light: 0x053245 },
  { path: 'reload.limits.ink', role: 'text', dark: 0xa3cfe6, light: 0x053245 },
  { path: 'reload.limits.flash', role: 'core', dark: 0xffffff, light: 0x2e2e2e },
  { path: 'reload.limits.faint', role: 'faint', dark: 0x4c7b93, light: 0x4c7b93 },
  { path: 'reload.limits.deep', role: 'foot', dark: 0x020b12, light: 0xfff6e1 },
  { path: 'reload.limits.ground', role: 'paper', dark: 0x0a1620, light: 0xfaf1dc },
  { path: 'gather.live_lane.bright', role: 'core', dark: 0xffffff, light: 0x2e2e2e },
  { path: 'terrain.ridge_fill', role: 'rock', dark: 0x16222c, light: 0xc6bda8 },
  { path: 'terrain.ridge_rim', role: 'rim', dark: 0x4a6274, light: 0x41586a },
  { path: 'terrain.rings.0', role: 'ramp', dark: 0x2c3f4f, light: 0xbad1e5 },
  { path: 'terrain.rings.1', role: 'ramp', dark: 0x3d566a, light: 0x8ca7be },
  { path: 'terrain.rings.2', role: 'ramp', dark: 0x587891, light: 0x5f8099 },
  { path: 'terrain.rings.3', role: 'ramp', dark: 0x7fa2ba, light: 0x395a6f },
  { path: 'terrain.trench_floor', role: 'hole', dark: 0x03141f, light: 0xfff8e6 },
  { path: 'terrain.trench_rim', role: 'trim', dark: 0x4f8ba6, light: 0x2e6b85 },
  { path: 'terrain.void_color', role: 'foot', dark: 0x000204, light: 0xfff6e1 },
  { path: 'terrain.tick_root_color', role: 'dim', dark: 0x2f5f78, light: 0x2e5e77 },
  { path: 'terrain.tick_tip_color', role: 'dim', dark: 0x2f5f78, light: 0x2e5e77 },
  { path: 'terrain.style.chart.ridge.fill', role: 'rock', dark: 0x243544, light: 0xc6bda8 },
  { path: 'terrain.style.chart.ridge.rim', role: 'rim', dark: 0xa3cfe6, light: 0x325b6f },
  { path: 'terrain.style.chart.trench.floor', role: 'hole', dark: 0x020b12, light: 0xfff8e6 },
  { path: 'terrain.style.chart.trench.rim', role: 'trim', dark: 0x4f8ba6, light: 0x2e6b85 },
  { path: 'terrain.style.form.ridge.fill', role: 'rock', dark: 0x243544, light: 0xc6bda8 },
  { path: 'terrain.style.form.ridge.rim', role: 'rim', dark: 0x16222c, light: 0x4a5763 },
  { path: 'terrain.style.form.ridge.shadow', role: 'faint', dark: 0x00121c, light: 0x607986 },
  { path: 'terrain.style.form.ridge.flank.0', role: 'ramp', dark: 0x233748, light: 0x43586a },
  { path: 'terrain.style.form.ridge.flank.1', role: 'ramp', dark: 0x213443, light: 0x566b7c },
  { path: 'terrain.style.form.ridge.flank.2', role: 'ramp', dark: 0x1e303e, light: 0x6a7e8f },
  { path: 'terrain.style.form.ridge.flank.3', role: 'ramp', dark: 0x1a2a37, light: 0x7f92a2 },
  { path: 'terrain.style.form.ridge.flank.4', role: 'ramp', dark: 0x152430, light: 0x94a6b6 },
  { path: 'terrain.style.form.ridge.flank.5', role: 'ramp', dark: 0x101d28, light: 0xaabbca },
  { path: 'terrain.style.form.ridge.flank.6', role: 'ramp', dark: 0x0b1620, light: 0xc1d0de },
  { path: 'terrain.style.form.ridge.crest', role: 'trim', dark: 0x6f90aa, light: 0x486780 },
  { path: 'terrain.style.form.ridge.hairline', role: 'faint', dark: 0x8fb6cd, light: 0x557a8f },
  { path: 'terrain.style.form.trench.floor', role: 'hole', dark: 0x0d3444, light: 0xfff8e6 },
  { path: 'terrain.style.form.trench.ramp.0', role: 'ramp', dark: 0x092634, light: 0x3d5a6a },
  { path: 'terrain.style.form.trench.ramp.1', role: 'ramp', dark: 0x061c27, light: 0x677f8d },
  { path: 'terrain.style.form.trench.ramp.2', role: 'ramp', dark: 0x02101a, light: 0x92a7b5 },
  { path: 'terrain.style.form.trench.ramp.3', role: 'ramp', dark: 0x000406, light: 0xc2d1d6 },
  { path: 'terrain.style.form.trench.wall', role: 'trim', dark: 0x4f8ba6, light: 0x2e6b85 },
  { path: 'terrain.style.form.trench.rim', role: 'rim', dark: 0x061520, light: 0x465865 },
  { path: 'terrain.style.material.ridge.fill', role: 'rock', dark: 0x16222c, light: 0xc6bda8 },
  { path: 'terrain.style.material.ridge.rim', role: 'rim', dark: 0x0d161e, light: 0x4c5761 },
  { path: 'terrain.style.material.ridge.shadow', role: 'faint', dark: 0x00121c, light: 0x607986 },
  { path: 'terrain.style.material.ridge.chunk', role: 'rock', dark: 0x2b3d4d, light: 0xc6bda8 },
  { path: 'terrain.style.material.ridge.chunk_shadow', role: 'faint', dark: 0x0b141c, light: 0x6b7681 },
  { path: 'terrain.style.material.ridge.chunk_lit', role: 'trim', dark: 0x46617a, light: 0x4b6680 },
  { path: 'terrain.style.material.trench.floor', role: 'hole', dark: 0x03141f, light: 0xfff8e6 },
  { path: 'terrain.style.material.trench.shelf', role: 'trim', dark: 0x2f6885, light: 0x316a87 },
  { path: 'terrain.style.material.trench.band', role: 'dim', dark: 0x1e4256, light: 0x395d72 },
  { path: 'terrain.style.material.trench.lit_rim', role: 'trim', dark: 0x4f8ba6, light: 0x2e6b85 },
  { path: 'terrain.style.material.trench.rim', role: 'rim', dark: 0x061520, light: 0x465865 },
  { path: 'chart.magenta', role: 'mark', dark: 0xff5ec4, light: 0xc21a8e },
  { path: 'chart.depth_tints.historical.heat_ink.0', role: 'ramp', dark: 0xd2e6c8, light: 0x4a5b42 },
  { path: 'chart.depth_tints.historical.heat_ink.1', role: 'ramp', dark: 0x6fa89a, light: 0x50887b },
  { path: 'chart.depth_tints.historical.heat_ink.2', role: 'ramp', dark: 0x46689a, light: 0x81a5db },
  { path: 'chart.depth_tints.historical.heat_ink.3', role: 'ramp', dark: 0x2c2860, light: 0xc6c8ff },
  { path: 'chart.depth_tints.modern.heat_ink.0', role: 'ramp', dark: 0x9fe3d9, light: 0x1b625a },
  { path: 'chart.depth_tints.modern.heat_ink.1', role: 'ramp', dark: 0x2fa6a8, light: 0x048d8f },
  { path: 'chart.depth_tints.modern.heat_ink.2', role: 'ramp', dark: 0x2d5fbf, light: 0x70a2fd },
  { path: 'chart.depth_tints.modern.heat_ink.3', role: 'ramp', dark: 0x2a2370, light: 0xc4c9fe },
  { path: 'chart.torpedo_run.historical.ink', role: 'mark', dark: 0xe0705f, light: 0xb64a3b },
  { path: 'chart.torpedo_run.modern.ink', role: 'mark', dark: 0xe0705f, light: 0xb64a3b },
  { path: 'chart.battle_marks.modern.ink', role: 'mark', dark: 0xe0705f, light: 0xb64a3b },
  { path: 'identity.faction.1', role: 'faction', dark: 0x35e0d2, light: 0x1395b5 },
  { path: 'identity.faction.2', role: 'faction', dark: 0xffb454, light: 0xb8760a },
  { path: 'identity.faction.3', role: 'faction', dark: 0xff5c8a, light: 0xe93170 },
  { path: 'identity.faction.4', role: 'faction', dark: 0x7ee787, light: 0x1da138 },
  { path: 'identity.faction.5', role: 'faction', dark: 0xb18cff, light: 0x9368e4 },
  { path: 'identity.resource.1', role: 'resource', dark: 0xb9c7d2, light: 0x67737d },
  { path: 'identity.resource.2', role: 'resource', dark: 0x7ee787, light: 0x0f892c },
  { path: 'identity.resource.3', role: 'resource', dark: 0xb18cff, light: 0x7d57c4 },
  { path: 'identity.resource.4', role: 'resource', dark: 0xd8e84a, light: 0x707905 },
  { path: 'identity.resource.5', role: 'resource', dark: 0xd8c66a, light: 0x837206 },
];

/** The four depth bands of round 146, shallow first (shelf, slope,
 *  deep, abyss): each band of the modern depth tints, resolved over
 *  the sea to one opaque ink, in the dark identity and on paper. */
export const DEPTH_BAND_DARK = [0x183349, 0x0d2437, 0x06182a, 0x051220] as const;
export const DEPTH_BAND_LIGHT = [0xb7d7f3, 0xc7e4fd, 0xd7eaff, 0xf9f0da] as const;

/** The rule of the light chrome ([light.chrome], R3298). `worst` is the
 *  light ridge rock, the darkest map fill under a panel. */
export const LIGHT_CHROME_RULE = {
  rule: 'ladder_own_chroma',
  paperH: 88,
  paperC: 0.03,
  ground: { paperW: 0, themeW: 0.35 },
  ladder: { panel: 0.99, sheet: 0.965, ink: 0.96, deep: 0.975 },
  panelAlpha: 0.96,
  inkLadder: { text: 0.3, faint: 0.56, accent: 0.5 },
  edge: { ink: 'accent', alpha: 0.45 },
  frozenL: 0.52,
  floors: { text: 4.5, faint: 3, accent: 3, frozen: 4.5 },
  nudge: 0.01,
  worst: 0xc6bda8,
} as const;

/** One UI theme body: the eight inks as `#rrggbb` and the two alphas
 *  (the shape of ThemeBody in ui/theme.ts). */
export interface ChromeBody {
  readonly accent: string;
  readonly text: string;
  readonly dim: string;
  readonly faint: string;
  readonly panel: string;
  readonly sheet: string;
  readonly ink: string;
  readonly deep: string;
  readonly panelAlpha: number;
  readonly edgeAlpha: number;
}

/** One UI theme in both identities. `accentSrc` is the ink the light
 *  accent takes its hue and chroma from. */
export interface ChromeRow {
  readonly id: string;
  readonly accentSrc: string;
  readonly dark: ChromeBody;
  readonly light: ChromeBody;
}

/** AUTO and the five faction themes: `dark` is palette(id) of
 *  ui/theme.ts, and the light accent follows the light faction ink. */
export const LIGHT_CHROME_FACTIONS: readonly ChromeRow[] = [
  {
    id: '1', accentSrc: '#1395b5',
    dark: { accent: '#35e0d2', text: '#a3cfe6', dim: '#5e89a0', faint: '#4c7b93', panel: '#061623', sheet: '#04121d', ink: '#040f1a', deep: '#020b12', panelAlpha: 0.82, edgeAlpha: 0.25 },
    light: { accent: '#026e88', text: '#053245', dim: '#3f6d84', faint: '#4c7b93', panel: '#f9fcff', sheet: '#edf5fa', ink: '#ecf3f9', deep: '#f2f8fc', panelAlpha: 0.96, edgeAlpha: 0.45 },
  },
  {
    id: '2', accentSrc: '#b8760a',
    dark: { accent: '#ffb454', text: '#c6cca3', dim: '#80875f', faint: '#72794d', panel: '#101706', sheet: '#0d1304', ink: '#0a1104', deep: '#080b02', panelAlpha: 0.82, edgeAlpha: 0.25 },
    light: { accent: '#895707', text: '#2d310c', dim: '#656b41', faint: '#72794d', panel: '#f9fdf5', sheet: '#f1f5ed', ink: '#eff3ec', deep: '#f6f8f1', panelAlpha: 0.96, edgeAlpha: 0.45 },
  },
  {
    id: '3', accentSrc: '#e93170',
    dark: { accent: '#ff5c8a', text: '#e7bea7', dim: '#9f7962', faint: '#926a51', panel: '#1f1104', sheet: '#1a0d03', ink: '#160b02', deep: '#110702', panelAlpha: 0.82, edgeAlpha: 0.25 },
    light: { accent: '#b60750', text: '#442511', dim: '#835d45', faint: '#926a51', panel: '#fffbf7', sheet: '#f9f2ec', ink: '#f7f1eb', deep: '#fbf5f1', panelAlpha: 0.96, edgeAlpha: 0.45 },
  },
  {
    id: '4', accentSrc: '#1da138',
    dark: { accent: '#7ee787', text: '#9dd4d0', dim: '#578e8b', faint: '#44807d', panel: '#00191b', sheet: '#001416', ink: '#001114', deep: '#000d0d', panelAlpha: 0.82, edgeAlpha: 0.25 },
    light: { accent: '#017922', text: '#013634', dim: '#38726f', faint: '#44807d', panel: '#f3feff', sheet: '#ecf6f6', ink: '#ebf4f5', deep: '#f1f9f8', panelAlpha: 0.96, edgeAlpha: 0.45 },
  },
  {
    id: '5', accentSrc: '#9368e4',
    dark: { accent: '#b18cff', text: '#e2bad2', dim: '#9c758c', faint: '#8f657e', panel: '#210e15', sheet: '#1b0a12', ink: '#18080e', deep: '#11060c', panelAlpha: 0.82, edgeAlpha: 0.25 },
    light: { accent: '#7143bc', text: '#402236', dim: '#815870', faint: '#90657e', panel: '#fffafc', sheet: '#faf0f4', ink: '#f8eff2', deep: '#fbf5f8', panelAlpha: 0.96, edgeAlpha: 0.45 },
  },
];

/** The vendored UI themes of themes.toml: each keeps its own accent. */
export const LIGHT_CHROME_VENDORED: readonly ChromeRow[] = [
  {
    id: 'blue', accentSrc: '#759ee9',
    dark: { accent: '#759ee9', text: '#b3c2db', dim: '#6c7c96', faint: '#5d6d87', panel: '#050d1a', sheet: '#050915', ink: '#030711', deep: '#010409', panelAlpha: 0.82, edgeAlpha: 0.25 },
    light: { accent: '#3c61a7', text: '#222e42', dim: '#586881', faint: '#657590', panel: '#fafcff', sheet: '#f0f3fa', ink: '#eef2f8', deep: '#f3f7fb', panelAlpha: 0.96, edgeAlpha: 0.45 },
  },
  {
    id: 'graphite', accentSrc: '#c4c4c4',
    dark: { accent: '#c4c4c4', text: '#b4b4b4', dim: '#767676', faint: '#696969', panel: '#0d0d0d', sheet: '#0a0a0a', ink: '#070707', deep: '#030303', panelAlpha: 0.86, edgeAlpha: 0.18 },
    light: { accent: '#636363', text: '#2e2e2e', dim: '#676767', faint: '#747474', panel: '#fcfcfc', sheet: '#f3f3f3', ink: '#f2f2f2', deep: '#f7f7f7', panelAlpha: 0.96, edgeAlpha: 0.45 },
  },
];

/** The frozen chrome inks in both identities. `step` is `frozen` (the
 *  frozen lightness and floor) or `text` (a near-white ink: the text
 *  step and floor). Each clears its floor on the grounds of every row
 *  above, and no theme moves it. */
export const LIGHT_CHROME_FROZEN = [
  { token: 'bad', step: 'frozen', dark: '#ff5c8a', light: '#be1356' },
  { token: 'good', step: 'frozen', dark: '#7ee787', light: '#007f25' },
  { token: 'gold', step: 'frozen', dark: '#d8c66a', light: '#796904' },
  { token: 'amber', step: 'frozen', dark: '#ffb454', light: '#915b00' },
  { token: 'violet', step: 'frozen', dark: '#b18cff', light: '#754eba' },
  { token: 'hero-ink', step: 'text', dark: '#eafcfa', light: '#233130' },
  { token: 'hover-ink', step: 'text', dark: '#d8f4f0', light: '#1c3330' },
  { token: 'readout-white', step: 'text', dark: '#ffffff', light: '#2e2e2e' },
] as const;
